import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../../ui/dialog';
import { Badge } from '../../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Search, Calendar, Phone, Mail, User, FileText, Eye } from 'lucide-react';
import AdminNavbar from '../../../AdminNavbar';
import { bookingService } from '../../../../services';
import { useDataFetching } from '../../../../hooks';
import { getStatusBadge, formatDate, formatDateTime, filterDataBySearch } from '../../../../utils';
import { demoBookings } from '../../../../constants';
import { toast } from 'sonner';
import { BookingTableSkeleton } from '../../../ui/table-skeleton';

const AdminBookings = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Use custom hook for data fetching
  const { data: bookings, isLoading, setData: setBookings } = useDataFetching(
    bookingService.getAllBookings,
    demoBookings,
    { errorMessage: 'Failed to load bookings. Using demo data.' }
  );

  // Filter bookings based on search term
  const filteredBookings = useMemo(() => {
    if (!Array.isArray(bookings)) {
      return [];
    }
    
    let filtered = bookings;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (booking) =>
          booking.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking.treatmentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking.patientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking.patientMobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          booking._id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [searchTerm, bookings]);

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setIsModalOpen(true);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      // Update via API
      await bookingService.updateBookingStatus(bookingId, newStatus);
      
      // Update local state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, status: newStatus }
            : booking
        )
      );
      
      // Update selected booking if it's the one being viewed
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: newStatus }));
      }
      
      toast.success('Booking status updated successfully!');
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update booking status. Please try again.');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    try {
      await bookingService.deleteBooking(bookingId);
      
      // Remove from local state
      setBookings(prevBookings => 
        prevBookings.filter(booking => booking._id !== bookingId)
      );
      
      // Close modal if the deleted booking was being viewed
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking(null);
        setIsModalOpen(false);
      }
      
      toast.success('Booking deleted successfully!');
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error('Failed to delete booking. Please try again.');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    const reason = window.prompt('Please provide a reason for cancellation:');
    if (!reason) return;

    try {
      await bookingService.cancelBooking(bookingId, reason);
      
      // Update local state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { ...booking, status: 'cancelled', cancellationReason: reason }
            : booking
        )
      );
      
      // Update selected booking if it's the one being viewed
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking(prev => ({ 
          ...prev, 
          status: 'cancelled', 
          cancellationReason: reason 
        }));
      }
      
      toast.success('Booking cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Failed to cancel booking. Please try again.');
    }
  };

  const handleRescheduleBooking = async (bookingId) => {
    const newDate = window.prompt('Enter new date (YYYY-MM-DD):');
    const newTime = window.prompt('Enter new time (HH:MM AM/PM):');
    
    if (!newDate || !newTime) return;

    try {
      await bookingService.rescheduleBooking(bookingId, newDate, newTime);
      
      // Update local state
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking._id === bookingId 
            ? { 
                ...booking, 
                preferredDate: newDate, 
                preferredTime: newTime,
                status: 'rescheduled'
              }
            : booking
        )
      );
      
      // Update selected booking if it's the one being viewed
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking(prev => ({ 
          ...prev, 
          preferredDate: newDate,
          preferredTime: newTime,
          status: 'rescheduled'
        }));
      }
      
      toast.success('Booking rescheduled successfully!');
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      toast.error('Failed to reschedule booking. Please try again.');
    }
  };

  const handleSendReminder = async (bookingId) => {
    try {
      await bookingService.sendBookingReminder(bookingId);
      toast.success('Reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Bookings Management</h1>
          <p className="text-gray-600 mt-2">View and manage all patient bookings</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Bookings</CardTitle>
            <CardDescription>
              Search and view all patient booking information
            </CardDescription>
            <div className="flex items-center space-x-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <BookingTableSkeleton />
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient Name</TableHead>
                        <TableHead>Treatment Type</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Preferred Date</TableHead>
                        <TableHead>Booking ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBookings.map((booking) => (
                        <TableRow key={booking._id}>
                          <TableCell className="font-medium">{booking.patientName}</TableCell>
                          <TableCell>{booking.treatmentType}</TableCell>
                          <TableCell>{booking.patientEmail}</TableCell>
                          <TableCell>{booking.patientMobile}</TableCell>
                          <TableCell>{formatDate(booking.preferredDate)}</TableCell>
                          <TableCell className="font-mono text-sm">{booking._id}</TableCell>
                          <TableCell>{getStatusBadge(booking.status)}</TableCell>
                          <TableCell>
                            <Dialog open={isModalOpen && selectedBooking?._id === booking._id} onOpenChange={setIsModalOpen}>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleBookingClick(booking)}
                                >
                                  <Eye className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-2xl">
                                <DialogHeader>
                                  <DialogTitle>Booking Details</DialogTitle>
                                  <DialogDescription>
                                    Complete information for booking {booking._id}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-6">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">Patient Name:</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-6">{booking.patientName}</p>
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">Treatment Type:</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-6">{booking.treatmentType}</p>
                                    </div>

                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2">
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">Email:</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-6">{booking.patientEmail}</p>
                                    </div>

                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2">
                                        <Phone className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">Phone:</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-6">{booking.patientMobile}</p>
                                    </div>

                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">Preferred Date:</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-6">{formatDate(booking.preferredDate)}</p>
                                    </div>

                                    <div className="space-y-2">
                                      <div className="flex items-center space-x-2">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">Booking ID:</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-6 font-mono">{booking._id}</p>
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-4 w-4 text-muted-foreground" />
                                      <span className="font-medium">Symptoms:</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground ml-6">{booking.symptoms}</p>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <FileText className="h-4 w-4 text-muted-foreground" />
                                      <span className="font-medium">Previous Treatment:</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground ml-6">{booking.previousTreatment}</p>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <Calendar className="h-4 w-4 text-muted-foreground" />
                                      <span className="font-medium">Created At:</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground ml-6">{formatDateTime(booking.createdAt)}</p>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-medium">Status:</span>
                                    </div>
                                    <div className="ml-6 flex items-center space-x-3">
                                      <div>{getStatusBadge(booking.status)}</div>
                                      <Select
                                        value={booking.status}
                                        onValueChange={(value) => handleStatusUpdate(booking._id, value)}
                                      >
                                        <SelectTrigger className="w-32">
                                          <SelectValue placeholder="Update Status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="pending">Pending</SelectItem>
                                          <SelectItem value="confirmed">Confirmed</SelectItem>
                                          <SelectItem value="completed">Completed</SelectItem>
                                          <SelectItem value="cancelled">Cancelled</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>

                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-medium">Actions:</span>
                                    </div>
                                    <div className="ml-6 flex flex-wrap gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleSendReminder(booking._id)}
                                      >
                                        Send Reminder
                                      </Button>
                                      {booking.status !== 'cancelled' && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleCancelBooking(booking._id)}
                                        >
                                          Cancel
                                        </Button>
                                      )}
                                      {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleRescheduleBooking(booking._id)}
                                        >
                                          Reschedule
                                        </Button>
                                      )}
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleDeleteBooking(booking._id)}
                                      >
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {filteredBookings.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">
                      {searchTerm 
                        ? "No bookings found matching your search criteria." 
                        : "No bookings available at the moment."}
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminBookings;
